// Admin coupons (/admin/coupons) — marketing discount-code catalog: create,
// list, and toggle active. NOT the license/order expiry model retired by
// docs/LICENSING.md R1-R5 — starts_at/expires_at here gate a promotion
// window; a coupon lapsing never touches entitlement duration (entitlements are
// perpetual and cover every future update — patch, minor AND major — regardless
// of any coupon's validity window).
// Whether checkout accepts these codes depends on the API's SALES_ENABLED switch.
// This screen does not assert that either way: it is live API state, never a fact
// to hardcode into the catalog UI.

import {
  Fragment,
  useState,
  type Dispatch,
  type FormEvent,
  type ReactNode,
  type SetStateAction,
} from "react";
import { dataClass } from "@ahcut/ui/recipes";
import { Alert, Badge, Button, Checkbox, Field, Select } from "@/ui";
import { cn } from "@/lib/cn";
import { PageHeader } from "@/components/PageHeader";
import { Loading, ErrorState } from "@/components/States";
import { Pagination } from "@/components/Pagination";
import { useAsync } from "@/lib/useAsync";
import { createAdminCoupon, listAdminCoupons, updateAdminCoupon } from "@/lib/admin";
import {
  couponKindLabel,
  couponKindOptions,
  couponPeriod,
  couponQuotaLabel,
  couponValueLabel,
} from "@/lib/format";
import type { AdminCoupon, AdminCouponInput, CouponKind, Paged } from "@/lib/types";
import { AdminPanel, PanelBody, PanelEmpty } from "@/components/admin/AdminPanel";
import { DataCell, DataRow, DataTable, type DataColumn } from "@/components/admin/DataTable";

const PAGE = 25;
const KIND_OPTIONS = couponKindOptions();

// Tipe sits under Nilai ("20%" / "Rp 50.000" already reads as the kind) and the
// quota under Kode, which keeps the three row actions on one line and the table
// tabular from the 58rem step (the 1280 laptop panel).
const COLUMNS: readonly DataColumn[] = [
  { label: "Kode" },
  { label: "Nilai", align: "end" },
  { label: "Periode" },
  { label: "Status" },
  { label: "Aksi" },
];

/** Busy label for a quick-toggle row action: the visible "…" stays, and screen
 *  readers hear what is happening instead of an ellipsis. */
function BusyText({ busy, children }: { busy: boolean; children: ReactNode }) {
  if (!busy) return <>{children}</>;
  return (
    <>
      <span aria-hidden="true">…</span>
      <span className="sr-only">Memproses</span>
    </>
  );
}

/** <input type="datetime-local"> (local wall-clock, no offset) → RFC3339 UTC
 *  the API can parse, or null for an untouched/empty field (open-ended bound). */
function localDateTimeToRFC3339(value: string): string | null {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

/** RFC3339 (as returned by the API) → <input type="datetime-local"> value in
 *  local wall-clock time, or "" for null/unparseable (open-ended bound). Inverse
 *  of localDateTimeToRFC3339 — used to pre-fill the edit form from a row. */
function rfc3339ToLocalDateTime(value: string | null): string {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n: number) => String(n).padStart(2, "0");
  return (
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` +
    `T${pad(d.getHours())}:${pad(d.getMinutes())}`
  );
}

interface FormState {
  code: string;
  kind: CouponKind;
  value: string;
  maxRedemptions: string;
  startsAt: string;
  expiresAt: string;
  active: boolean;
}

const EMPTY_FORM: FormState = {
  code: "",
  kind: "percent",
  value: "",
  maxRedemptions: "",
  startsAt: "",
  expiresAt: "",
  active: true,
};

/** Fields shared by the create form and the inline edit row. Ids are scoped per
 *  form (`idPrefix`): both forms can be on screen together, and label-derived
 *  ids would collide. `leading` renders first in the grid (the create form's
 *  Kode — a code is immutable after create). */
function CouponFields({
  idPrefix,
  form,
  setForm,
  leading,
}: {
  idPrefix: string;
  form: FormState;
  setForm: Dispatch<SetStateAction<FormState>>;
  leading?: ReactNode;
}) {
  const percent = form.kind === "percent";
  return (
    <div className="flex flex-col gap-3">
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {leading}
        <Field id={`${idPrefix}-kind`} label="Tipe">
          {(control) => (
            <Select
              {...control}
              value={form.kind}
              onChange={(e) => setForm((f) => ({ ...f, kind: e.target.value as CouponKind }))}
            >
              {KIND_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </Select>
          )}
        </Field>
        <Field
          id={`${idPrefix}-value`}
          label={percent ? "Nilai (%)" : "Nilai (Rp)"}
          type="number"
          min={1}
          max={percent ? 99 : undefined}
          value={form.value}
          onChange={(e) => setForm((f) => ({ ...f, value: e.target.value }))}
          placeholder={percent ? "20" : "100000"}
          inputClassName={dataClass}
        />
        <Field
          id={`${idPrefix}-quota`}
          label="Kuota (opsional)"
          type="number"
          min={1}
          value={form.maxRedemptions}
          onChange={(e) => setForm((f) => ({ ...f, maxRedemptions: e.target.value }))}
          placeholder="Tanpa batas"
          inputClassName={dataClass}
        />
        <Field
          id={`${idPrefix}-starts`}
          label="Mulai (opsional)"
          type="datetime-local"
          value={form.startsAt}
          onChange={(e) => setForm((f) => ({ ...f, startsAt: e.target.value }))}
        />
        <Field
          id={`${idPrefix}-expires`}
          label="Berakhir (opsional)"
          type="datetime-local"
          value={form.expiresAt}
          onChange={(e) => setForm((f) => ({ ...f, expiresAt: e.target.value }))}
        />
      </div>
      {/* The whole label is the hit area, so the 16px box still gets a 44px target. */}
      <label className="inline-flex min-h-11 w-fit cursor-pointer items-center gap-2.5 text-sm font-medium text-fg-0">
        <Checkbox
          checked={form.active}
          onChange={(e) => setForm((f) => ({ ...f, active: e.target.checked }))}
        />
        Aktif
      </label>
    </div>
  );
}

export function AdminCoupons() {
  const [offset, setOffset] = useState(0);
  const { data, loading, error, reload } = useAsync<Paged<AdminCoupon>>(
    (s) => listAdminCoupons({ limit: PAGE, offset }, s),
    [offset],
  );

  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  const [togglingId, setTogglingId] = useState<string | null>(null);
  const [toggleError, setToggleError] = useState<string | null>(null);

  const [featuringId, setFeaturingId] = useState<string | null>(null);
  const [featureError, setFeatureError] = useState<string | null>(null);

  const [editingId, setEditingId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<FormState>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);

  async function handleCreate(e: FormEvent) {
    e.preventDefault();
    setCreating(true);
    setCreateError(null);
    try {
      const value = Number(form.value);
      if (!form.code.trim() || !Number.isFinite(value)) {
        throw new Error("Kode dan nilai wajib diisi.");
      }
      const maxRedemptions =
        form.maxRedemptions.trim() === "" ? null : Number(form.maxRedemptions);
      const input: AdminCouponInput = {
        code: form.code.trim(),
        kind: form.kind,
        value,
        max_redemptions: maxRedemptions,
        starts_at: localDateTimeToRFC3339(form.startsAt),
        expires_at: localDateTimeToRFC3339(form.expiresAt),
        active: form.active,
      };
      await createAdminCoupon(input);
      setForm(EMPTY_FORM);
      setOffset(0);
      reload();
    } catch (err) {
      setCreateError(err instanceof Error ? err.message : String(err));
    } finally {
      setCreating(false);
    }
  }

  // Quick toggle, kept alongside the full edit form below for fast on/off
  // without opening the row. UpdateCoupon replaces the whole row, so the other
  // fields are echoed back unchanged — only `active` flips.
  async function handleToggleActive(c: AdminCoupon) {
    setTogglingId(c.id);
    setToggleError(null);
    try {
      await updateAdminCoupon(c.id, {
        kind: c.kind,
        value: c.value,
        max_redemptions: c.max_redemptions,
        starts_at: c.starts_at,
        expires_at: c.expires_at,
        active: !c.active,
      });
      reload();
    } catch (err) {
      setToggleError(err instanceof Error ? err.message : String(err));
    } finally {
      setTogglingId(null);
    }
  }

  // Jadikan/lepas unggulan. PATCH full-replace + featured; server melepas
  // pemegang lama dalam tx yang sama (maks. satu kupon unggulan) dan
  // menjadwalkan rebuild marketing.
  async function handleToggleFeatured(c: AdminCoupon) {
    setFeaturingId(c.id);
    setFeatureError(null);
    try {
      await updateAdminCoupon(c.id, {
        kind: c.kind,
        value: c.value,
        max_redemptions: c.max_redemptions,
        starts_at: c.starts_at,
        expires_at: c.expires_at,
        active: c.active,
        featured: !c.featured,
      });
      reload();
    } catch (err) {
      setFeatureError(err instanceof Error ? err.message : String(err));
    } finally {
      setFeaturingId(null);
    }
  }

  // Full inline edit: kind/value/quota/window/active, opened per row. Code and
  // redeemed_count are immutable — code shows as the form's read-only label,
  // redeemed_count isn't shown here at all. Submit sends the FULL input (the
  // API PATCH is a full-replace body, same as the toggle above).
  function handleStartEdit(c: AdminCoupon) {
    setEditingId(c.id);
    setEditError(null);
    setEditForm({
      code: c.code,
      kind: c.kind,
      value: String(c.value),
      maxRedemptions: c.max_redemptions == null ? "" : String(c.max_redemptions),
      startsAt: rfc3339ToLocalDateTime(c.starts_at),
      expiresAt: rfc3339ToLocalDateTime(c.expires_at),
      active: c.active,
    });
  }

  function handleCancelEdit() {
    setEditingId(null);
    setEditError(null);
  }

  async function handleSaveEdit(e: FormEvent, id: string) {
    e.preventDefault();
    setSaving(true);
    setEditError(null);
    try {
      const value = Number(editForm.value);
      if (!Number.isFinite(value)) {
        throw new Error("Nilai wajib diisi.");
      }
      const maxRedemptions =
        editForm.maxRedemptions.trim() === "" ? null : Number(editForm.maxRedemptions);
      const input: AdminCouponInput = {
        kind: editForm.kind,
        value,
        max_redemptions: maxRedemptions,
        starts_at: localDateTimeToRFC3339(editForm.startsAt),
        expires_at: localDateTimeToRFC3339(editForm.expiresAt),
        active: editForm.active,
      };
      await updateAdminCoupon(id, input);
      setEditingId(null);
      reload();
    } catch (err) {
      setEditError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  }

  if (loading && !data) return <Loading label="Memuat kupon…" />;
  if (error) return <ErrorState error={error} onRetry={reload} />;

  return (
    <div className="flex min-w-0 flex-col gap-6">
      <PageHeader
        title="Kupon"
        lede="Katalog kode diskon. Jendela mulai/berakhir hanya mengatur masa promosi — bukan masa berlaku lisensi. Maksimal satu kupon unggulan."
      />

      <AdminPanel title="Buat kupon baru">
        <PanelBody>
          <form className="flex flex-col gap-4" onSubmit={handleCreate} aria-label="Buat kupon baru">
            <CouponFields
              idPrefix="coupon-new"
              form={form}
              setForm={setForm}
              leading={
                <Field
                  id="coupon-new-code"
                  label="Kode"
                  value={form.code}
                  onChange={(e) => setForm((f) => ({ ...f, code: e.target.value.toUpperCase() }))}
                  placeholder="mis. KODE10"
                  maxLength={32}
                  inputClassName={dataClass}
                />
              }
            />
            {createError && (
              <Alert tone="danger" role="alert">
                {createError}
              </Alert>
            )}
            <div>
              <Button type="submit" variant="secondary" disabled={creating}>
                {creating ? "Membuat…" : "Buat kupon"}
              </Button>
            </div>
          </form>
        </PanelBody>
      </AdminPanel>

      <div className="flex min-w-0 flex-col gap-4">
        <AdminPanel title="Daftar kupon">
          {!data || data.items.length === 0 ? (
            <PanelEmpty>Belum ada kupon. Buat kupon pertama lewat form di atas.</PanelEmpty>
          ) : (
            <DataTable label="Daftar kupon" columns={COLUMNS} stackBelow="58rem" busy={loading}>
              {data.items.map((c) => {
                const editing = editingId === c.id;
                const editRowId = `coupon-edit-${c.id}`;
                const period = couponPeriod(c.starts_at, c.expires_at);
                return (
                  <Fragment key={c.id}>
                    <DataRow>
                      {/* The code on one line in table layout (it never splits
                          mid-word; only one longer than the phone column may
                          break), its quota under it. */}
                      <DataCell label="Kode" className="whitespace-nowrap">
                        <span className={cn(dataClass, "block font-medium text-fg-0")}>{c.code}</span>
                        <span className="mt-0.5 block text-xs text-fg-2">
                          Kuota{" "}
                          <span className={dataClass}>
                            {couponQuotaLabel(c.redeemed_count, c.max_redemptions)}
                          </span>
                        </span>
                      </DataCell>
                      {/* Block lines follow the cell's alignment: right in table
                          layout, left once the rows stack. */}
                      <DataCell label="Nilai" align="end" className="whitespace-nowrap">
                        <span className={cn(dataClass, "block text-fg-0")}>
                          {couponValueLabel(c.kind, c.value)}
                        </span>
                        <span className="mt-0.5 block text-xs text-fg-2">
                          {couponKindLabel(c.kind)}
                        </span>
                      </DataCell>
                      <DataCell label="Periode">
                        {/* Line one: the lead word (body face) or "start –";
                            line two: the date. Only the dates are mono
                            (DESIGN.md §4). The column keeps the first line on
                            the stacked label's baseline. */}
                        <span className="flex flex-col">
                          {period.lead && <span>{period.lead}</span>}
                          {period.dates.map((date, i) => (
                            <span key={i} className={dataClass}>
                              {i < period.dates.length - 1 ? `${date} –` : date}
                            </span>
                          ))}
                        </span>
                      </DataCell>
                      <DataCell label="Status">
                        <span className="flex flex-col items-start gap-1.5">
                          <Badge tone={c.active ? "ok" : "neutral"} dot>
                            {c.active ? "Aktif" : "Nonaktif"}
                          </Badge>
                          {c.featured && (
                            <Badge tone="signal" dot>
                              Unggulan
                            </Badge>
                          )}
                        </span>
                      </DataCell>
                      <DataCell actions>
                        <div className="flex flex-wrap items-center gap-2">
                          <Button
                            variant="quiet"
                            size="sm"
                            onClick={() => handleToggleActive(c)}
                            disabled={togglingId === c.id}
                          >
                            <BusyText busy={togglingId === c.id}>
                              {c.active ? "Nonaktifkan" : "Aktifkan"}
                            </BusyText>
                            <span className="sr-only"> {c.code}</span>
                          </Button>
                          <Button
                            variant="quiet"
                            size="sm"
                            onClick={() => handleToggleFeatured(c)}
                            disabled={featuringId === c.id}
                          >
                            <BusyText busy={featuringId === c.id}>
                              {c.featured ? "Lepas unggulan" : "Jadikan unggulan"}
                            </BusyText>
                            <span className="sr-only"> {c.code}</span>
                          </Button>
                          <Button
                            variant="quiet"
                            size="sm"
                            aria-expanded={editing}
                            aria-controls={editing ? editRowId : undefined}
                            onClick={() => (editing ? handleCancelEdit() : handleStartEdit(c))}
                          >
                            {editing ? "Tutup" : "Edit"}
                            <span className="sr-only"> {c.code}</span>
                          </Button>
                        </div>
                      </DataCell>
                    </DataRow>
                    {editing && (
                      <DataRow className="bg-surface-1 hover:bg-surface-1">
                        <DataCell id={editRowId} colSpan={COLUMNS.length}>
                          <form
                            className="flex flex-col gap-4 py-2"
                            onSubmit={(e) => handleSaveEdit(e, c.id)}
                            aria-labelledby={`${editRowId}-title`}
                          >
                            <h3
                              id={`${editRowId}-title`}
                              className="text-base font-semibold text-fg-0"
                            >
                              Ubah kupon <span className={dataClass}>{c.code}</span>
                            </h3>
                            <CouponFields
                              idPrefix={editRowId}
                              form={editForm}
                              setForm={setEditForm}
                            />
                            {editError && (
                              <Alert tone="danger" role="alert">
                                {editError}
                              </Alert>
                            )}
                            <div className="flex flex-wrap items-center gap-3">
                              <Button type="submit" variant="secondary" disabled={saving}>
                                {saving ? "Menyimpan…" : "Simpan"}
                              </Button>
                              <Button
                                type="button"
                                variant="quiet"
                                onClick={handleCancelEdit}
                                disabled={saving}
                              >
                                Batal
                              </Button>
                            </div>
                          </form>
                        </DataCell>
                      </DataRow>
                    )}
                  </Fragment>
                );
              })}
            </DataTable>
          )}
        </AdminPanel>
        {toggleError && (
          <Alert tone="danger" role="alert">
            {toggleError}
          </Alert>
        )}
        {featureError && (
          <Alert tone="danger" role="alert">
            {featureError}
          </Alert>
        )}
        {data && data.items.length > 0 && (
          <Pagination
            total={data.total}
            limit={data.limit}
            offset={data.offset}
            onPage={setOffset}
            disabled={loading}
          />
        )}
      </div>
    </div>
  );
}
